/**
 * API calls for the student dashboard — pending invites and code submission.
 */

import { api } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type { DashboardStatusResponse, SubmitCodeResponse } from 'api/arcMosaico/types'
import { fetchMyProjects } from 'pages/ArcProjectsList/services/arcProjectsApi'
import type { ArcProject } from 'pages/ArcProjectsList/types'

function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

export async function getDashboardStatus(): Promise<DashboardStatusResponse> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.studentInvites.dashboardStatus)
  assertAuthenticated(response)
  return response.data as DashboardStatusResponse
}

export async function submitInviteCode(inviteCode: string): Promise<SubmitCodeResponse> {
  const response = await api.post(ARC_MOSAICO_ENDPOINTS.studentInvites.submitCode, {
    inviteCode,
  })
  assertAuthenticated(response)
  return response.data as SubmitCodeResponse
}

/**
 * Fetch the projects assigned to the current student.
 *
 * Reuses the legacy `GET /api/arc_mosaico/projects?status=assigned` endpoint
 * (via `fetchMyProjects`). The backend maps `status=assigned` → `approved` and,
 * for the STUDENT profile, scopes the result through `project_students`, so the
 * student only receives their own assigned projects. No new endpoint is created.
 *
 * Defensive: never throws — returns an empty list on failure so a fetch error
 * isolates itself and does not crash the dashboard.
 */
export async function getMyAssignedProjects(): Promise<ArcProject[]> {
  const result = await fetchMyProjects({ status: 'assigned' })
  return result?.projects ?? []
}
